import React from 'react';
import { shouldRevealCapacity } from '../utils/capacityVisibility';
import './CapacityBadge.css';

interface CapacityBadgeProps {
  /** Current number of enrolled members. */
  enrolled: number;
  /** Maximum capacity, or null for unlimited (renders nothing). */
  capacity: number | null;
  /**
   * Admin surfaces pass this to always show the exact count. Public surfaces
   * leave it off so a near-empty course does not advertise how empty it is.
   */
  showExact?: boolean;
}

/**
 * Reusable "X/Y" capacity pill that mirrors the .event-card-badge visual
 * language from PublicEventsListPage (uppercase pill, green/amber tints).
 *
 * States:
 *  - capacity null → renders nothing (unlimited capacity).
 *  - below the reveal ratio, not full, not `showExact` → renders nothing.
 *  - full (enrolled >= capacity) → amber "full" tint.
 *  - almost full (>= 80%) → amber "full" tint.
 *  - otherwise → green "available" tint.
 */
const CapacityBadge: React.FC<CapacityBadgeProps> = ({ enrolled, capacity, showExact = false }) => {
  if (capacity == null) return null;

  const isFull = enrolled >= capacity;
  if (!showExact && !isFull && !shouldRevealCapacity(enrolled, capacity)) return null;

  const isAlmostFull = !isFull && capacity > 0 && enrolled / capacity >= 0.8;
  const modifier = isFull
    ? 'capacity-badge--full'
    : isAlmostFull
    ? 'capacity-badge--almost'
    : 'capacity-badge--available';

  return (
    <span className={`capacity-badge ${modifier}`}>
      {enrolled}/{capacity}
    </span>
  );
};

export default CapacityBadge;
